import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const ADMIN_BASE = `${BACKEND}/api/admin`;

export const adminClient = axios.create({
  baseURL: ADMIN_BASE,
  withCredentials: true,
});

// Fallback token: quando i cookie sono bloccati (contesti embedded/cross-site),
// il token di sessione viene inviato via header Authorization.
const TOKEN_KEY = "rinova_admin_token";
export const getAdminToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setAdminToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage non disponibile: si usa solo il cookie */
  }
};
adminClient.interceptors.request.use((config) => {
  const t = getAdminToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ---- Auth ----
export const apiLogin = (email, password) =>
  adminClient.post("/auth/login", { email, password }).then((r) => {
    if (r.data && r.data.token) setAdminToken(r.data.token);
    return r.data;
  });
export const apiLogout = () =>
  adminClient.post("/auth/logout").then((r) => {
    setAdminToken(null);
    return r.data;
  });
export const apiMe = () => adminClient.get("/auth/me").then((r) => r.data);
export const apiChangePassword = (current_password, new_password) =>
  adminClient.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);

// ---- Meta / Dashboard ----
export const apiMeta = () => adminClient.get("/meta").then((r) => r.data);
export const apiDashboard = () => adminClient.get("/dashboard").then((r) => r.data);

// ---- Products ----
export const apiListProducts = (params) =>
  adminClient.get("/products", { params }).then((r) => r.data);
export const apiGetProduct = (id) => adminClient.get(`/products/${id}`).then((r) => r.data);
export const apiCreateProduct = (data) => adminClient.post("/products", data).then((r) => r.data);
export const apiUpdateProduct = (id, data) =>
  adminClient.patch(`/products/${id}`, data).then((r) => r.data);
export const apiDeleteProduct = (id) => adminClient.delete(`/products/${id}`).then((r) => r.data);
export const apiDuplicateProduct = (id) =>
  adminClient.post(`/products/${id}/duplicate`).then((r) => r.data);
export const apiBulkProducts = (ids, action, value) =>
  adminClient.post("/products/bulk", { ids, action, value }).then((r) => r.data);
export const apiGenerateVariants = (id, payload) =>
  adminClient.post(`/products/${id}/variants/generate`, payload).then((r) => r.data);

// ---- Uploads (Cloudinary via server) ----
export const apiUploadImage = (file, folder, onProgress, context) => {
  const fd = new FormData();
  fd.append("file", file);
  if (folder) fd.append("folder", folder);
  if (context) fd.append("context", context);
  return adminClient
    .post("/uploads", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    })
    .then((r) => r.data);
};
export const apiDeleteAsset = (public_id) =>
  adminClient.post("/uploads/delete", { public_id }).then((r) => r.data);

// ---- Aspetto del sito (asset/icone) ----
export const apiGetSiteAssets = () => adminClient.get("/site-assets").then((r) => r.data);
export const apiPutSiteAssets = (assets) => adminClient.put("/site-assets", { assets }).then((r) => r.data);

// ---- Footer (partner spedizioni + metodi di pagamento) ----
export const apiGetFooter = () => adminClient.get("/footer").then((r) => r.data);
export const apiSaveFooter = (payload) => adminClient.put("/footer", payload).then((r) => r.data);

// ---- Categories ----
export const apiListCategories = () => adminClient.get("/categories").then((r) => r.data);
export const apiCreateCategory = (data) => adminClient.post("/categories", data).then((r) => r.data);
export const apiUpdateCategory = (id, data) =>
  adminClient.patch(`/categories/${id}`, data).then((r) => r.data);
export const apiDeleteCategory = (id, moveTo) =>
  adminClient.delete(`/categories/${id}`, { params: moveTo ? { move_to: moveTo } : {} }).then((r) => r.data);
export const apiReorderCategories = (ids) =>
  adminClient.post("/categories/reorder", { ids }).then((r) => r.data);

// ---- Brands ----
export const apiListBrands = () => adminClient.get("/brands").then((r) => r.data);
export const apiCreateBrand = (data) => adminClient.post("/brands", data).then((r) => r.data);
export const apiUpdateBrand = (id, data) =>
  adminClient.patch(`/brands/${id}`, data).then((r) => r.data);
export const apiDeleteBrand = (id, moveTo) =>
  adminClient.delete(`/brands/${id}`, { params: moveTo ? { move_to: moveTo } : {} }).then((r) => r.data);
export const apiReorderBrands = (ids) =>
  adminClient.post("/brands/reorder", { ids }).then((r) => r.data);

export const CSV_EXPORT_URL = `${ADMIN_BASE}/export/catalog.csv`;

// ---- Settings ----
export const apiGetSettings = () => adminClient.get("/settings").then((r) => r.data);
export const apiUpdateSettings = (data) => adminClient.patch("/settings", data).then((r) => r.data);

// ---- Commerce (Ordini / Carrelli abbandonati / Pagamenti) ----
export const apiCommerceKpis = (days = 30) =>
  adminClient.get("/commerce/kpis", { params: { days } }).then((r) => r.data);
export const apiCommerceOrders = (params) =>
  adminClient.get("/commerce/orders", { params }).then((r) => r.data);
export const apiCommerceOrder = (orderNumber) =>
  adminClient.get(`/commerce/orders/${orderNumber}`).then((r) => r.data);
export const apiFulfillOrder = (orderNumber, fulfilled = true) =>
  adminClient.post(`/commerce/orders/${orderNumber}/fulfill`, { fulfilled }).then((r) => r.data);
export const apiRefundOrder = (orderNumber) =>
  adminClient.post(`/commerce/orders/${orderNumber}/refund`).then((r) => r.data);
export const apiUpdateOrderNotes = (orderNumber, notes) =>
  adminClient.patch(`/commerce/orders/${orderNumber}/notes`, { notes }).then((r) => r.data);
export const apiUpdateOrderShippingCost = (orderNumber, cents, fonte = "manuale") =>
  adminClient.patch(`/commerce/orders/${orderNumber}/shipping-cost`,
    { costo_spedizione_reale_cents: cents, fonte }).then((r) => r.data);
export const apiPaymentsDiagnostic = () =>
  adminClient.get("/payments/diagnostic").then((r) => r.data);
export const apiAnalytics = (days = 30) =>
  adminClient.get("/analytics", { params: { days } }).then((r) => r.data);
export const apiVerifyPayments = () =>
  adminClient.post("/payments/verify").then((r) => r.data);
export const apiReconcileOrder = (orderNumber) =>
  adminClient.post(`/commerce/orders/${orderNumber}/reconcile`).then((r) => r.data);
// ---- SumUp: monitor pagamenti in tempo reale ----
export const apiSumupOrders = (limit = 50) =>
  adminClient.get("/payments/orders", { params: { limit } }).then((r) => r.data);
export const apiSumupSummary = () =>
  adminClient.get("/payments/summary").then((r) => r.data);
export const apiSumupRecheck = (orderNumber) =>
  adminClient.post(`/payments/orders/${orderNumber}/recheck`).then((r) => r.data);
// ---- Vista Agenzia Meta ----
export const apiMetaReport = (days = 30) =>
  adminClient.get("/meta/report", { params: { days } }).then((r) => r.data);
export const apiMetaReportCsv = (days = 30) =>
  adminClient.get("/meta/report.csv", { params: { days }, responseType: "blob" }).then((r) => r.data);

// ---- Spedizioni GLS ----
export const apiGetShippingSettings = () =>
  adminClient.get("/shipping/settings").then((r) => r.data);
export const apiSaveShippingSettings = (data) =>
  adminClient.put("/shipping/settings", data).then((r) => r.data);
export const apiCreateShipment = (orderNumber) =>
  adminClient.post(`/commerce/orders/${orderNumber}/shipment`).then((r) => r.data);
export const apiGetShipment = (orderNumber) =>
  adminClient.get(`/commerce/orders/${orderNumber}/shipment`).then((r) => r.data);
export const apiBulkShipments = (orderNumbers) =>
  adminClient.post("/commerce/shipments/bulk", { order_numbers: orderNumbers }).then((r) => r.data);

// ---- Recupero carrelli ----
export const apiAdminEnvironment = () =>
  adminClient.get("/environment").then((r) => r.data);
export const apiRecoverySettings = () =>
  adminClient.get("/recovery/settings").then((r) => r.data);
export const apiSaveRecoverySettings = (data) =>
  adminClient.put("/recovery/settings", data).then((r) => r.data);
export const apiRecoverySessions = () =>
  adminClient.get("/recovery/sessions").then((r) => r.data);
export const apiStopRecoverySession = (id) =>
  adminClient.post(`/recovery/sessions/${id}/stop`).then((r) => r.data);
export const apiActivateRecovery = (cartId) =>
  adminClient.post("/recovery/activate", { cart_id: cartId }).then((r) => r.data);
export const apiRecoveryTest = (email, topic) =>
  adminClient.post("/recovery/test-email", { email, topic }).then((r) => r.data);
export const apiRecoveryAnalytics = () =>
  adminClient.get("/recovery/analytics").then((r) => r.data);
export const apiRecoveryProducts = () =>
  adminClient.get("/recovery/products").then((r) => r.data);
export const apiRecoveryPreview = (productId, topic, variant = {}) =>
  adminClient.post("/recovery/preview", {
    product_id: productId, topic,
    color_id: variant.color_id ?? null,
    memory: variant.memory ?? null,
    grade: variant.grade ?? null,
  }).then((r) => r.data);

// ---- Pubblicazione / Sincronizzazione contenuti ----
export const apiContentStatus = () => adminClient.get("/content/status").then((r) => r.data);
export const apiContentPreparePublish = () => adminClient.post("/content/prepare-publish").then((r) => r.data);
export const apiContentExport = () => adminClient.get("/content/export").then((r) => r.data);
export const apiContentImport = (payload) => adminClient.post("/content/import", payload).then((r) => r.data);

// ---- Homepage ----
export const apiGetHomepage = () => adminClient.get("/homepage").then((r) => r.data);
export const apiSaveHomepageDraft = (sections) =>
  adminClient.put("/homepage/draft", { sections }).then((r) => r.data);
export const apiPublishHomepage = () => adminClient.post("/homepage/publish").then((r) => r.data);
export const apiResetHomepageDefault = () => adminClient.post("/homepage/reset-default").then((r) => r.data);
export const apiGetHero = () => adminClient.get("/hero").then((r) => r.data);
export const apiSaveHero = (slides) => adminClient.put("/hero", { slides }).then((r) => r.data);
export const apiGetSectionMedia = () => adminClient.get("/section-media").then((r) => r.data);
export const apiSaveSectionMedia = (grades, devices) => adminClient.put("/section-media", { grades, devices }).then((r) => r.data);
export const apiSearchProducts = (q) =>
  adminClient.get("/search/products", { params: { q } }).then((r) => r.data);

// ---- Offers ----
export const apiOffersPreview = (payload) => adminClient.post("/offers/preview", payload).then((r) => r.data);
export const apiOffersApply = (payload) => adminClient.post("/offers/apply", payload).then((r) => r.data);
export const apiOffersList = () => adminClient.get("/offers").then((r) => r.data);
export const apiOffersEnd = (payload) => adminClient.post("/offers/end", payload).then((r) => r.data);

// ---- Catalog ordering ----
export const apiCategoryProducts = (id) => adminClient.get(`/categories/${id}/products`).then((r) => r.data);
export const apiSetCategoryOrder = (id, ids) => adminClient.post(`/categories/${id}/product-order`, { ids }).then((r) => r.data);
export const apiBrandProducts = (id) => adminClient.get(`/brands/${id}/products`).then((r) => r.data);
export const apiSetBrandOrder = (id, ids) => adminClient.post(`/brands/${id}/product-order`, { ids }).then((r) => r.data);

// ---- Reviews ----
export const apiListReviews = (params) => adminClient.get("/reviews", { params }).then((r) => r.data);
export const apiGetReview = (id) => adminClient.get(`/reviews/${id}`).then((r) => r.data);
export const apiCreateReview = (data) => adminClient.post("/reviews", data).then((r) => r.data);
export const apiUpdateReview = (id, data) => adminClient.patch(`/reviews/${id}`, data).then((r) => r.data);
export const apiDeleteReview = (id) => adminClient.delete(`/reviews/${id}`).then((r) => r.data);
export const apiReviewsBulk = (ids, action) => adminClient.post("/reviews/bulk", { ids, action }).then((r) => r.data);
export const apiDeleteAllReviews = (payload) => adminClient.post("/reviews/delete-all", payload).then((r) => r.data);
export const apiProductReviews = (productId) => adminClient.get(`/reviews/product/${productId}`).then((r) => r.data);
export const apiFeaturedReviews = () => adminClient.get("/reviews/featured").then((r) => r.data);
export const apiGenerateReviews = (payload) => adminClient.post("/reviews/generate", payload).then((r) => r.data);
export const apiGetProductFull = (id) => adminClient.get(`/products/${id}`).then((r) => r.data);

export const abbreviateName = (full) => {
  const parts = (full || "").trim().split(/\s+/);
  if (!parts[0]) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

// ---- Helpers ----
export const GRADE_LABELS = { nuovo: "Nuovo", like_new: "Come Nuovo", used: "Usato" };
export const GRADE_OPTIONS = [
  { value: "nuovo", label: "Nuovo" },
  { value: "like_new", label: "Come Nuovo" },
  { value: "used", label: "Usato" },
];

export const eur = (n) =>
  n == null || n === "" ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n));

export const errMsg = (e) =>
  e?.response?.data?.detail || "Si è verificato un errore. Riprova tra poco.";

// Trasformazione standard richiesta: stessa misura per tutte le foto.
export function cldThumb(url, t = "c_pad,w_1000,h_1000,b_transparent,f_auto,q_auto") {
  if (!url || typeof url !== "string" || !url.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/${t}/`);
}
